import { useQuery } from "@apollo/client";
import {
  faMagnifyingGlass,
  faSearch,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SubmitButton } from "Components/Button";
import { useDebounce } from "Components/Hooks/Hooks";
import { ButtonIcon } from "Components/Icon";
import { LcpUserPageWrapper } from "Components/MainPageWrapper";
import { Section } from "Components/Section";
import {
  Role,
  searchApplicationsQuery,
  SearchApplicationsQuery,
} from "generated/graphql";
import { createFieldProps } from "Inputs/FormTools";
import { loadingOption, Wordwheel } from "Inputs/Wordwheel";
import React, { useContext, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { serverConstants } from "Shared/Constants";
import { UserContext } from "Views/UserProvider";
import { AdminPaths } from "../Paths";
import { ApprovalsCollapsible } from "./ApprovalsCollapsible";
import { ReportingFieldset } from "./ReportingFieldset";

export function AdminDashboardPage() {
  const { user } = useContext(UserContext);

  return (
    <LcpUserPageWrapper
      className="adminDashboardWrapper"
      documentTitle="Admin Dashboard"
      theme={{ mainDivStyle: { maxWidth: "1000px" } }}
    >
      <Section title="Admin Dashboard" subtitle={serverConstants.InsuranceName}>
        <div className="columns is-tablet is-gapless">
          <div className="column adminDashboardColumn">
            <SearchFieldset />
            {serverConstants.ShowApprovalWorkflow && <ApprovalsCollapsible />}
          </div>
          <div className="column adminDashboardColumn">
            <ReportingFieldset />
            {user && user.roles.includes(Role.SUPER_ADMIN) && (
              <ManageAccountsFieldset />
            )}
          </div>
        </div>
      </Section>
    </LcpUserPageWrapper>
  );
}

const debounceTimeoutMS = 1500;

const phoneNumberSearchTextRegex = /^[()\-\s0-9]+$/;

export type ApplicationSearchResult =
  SearchApplicationsQuery["searchApplications"]["applications"][number];

// NOTE: this function should match SearchApplicationsQuery.cs
function getMatchedAppText(app: ApplicationSearchResult, searchText: string) {
  if (
    app.applicant.companyName.toUpperCase().includes(searchText.toUpperCase())
  ) {
    return app.applicant.companyName;
  } else if (
    app.applicant.emailAddress.toUpperCase().includes(searchText.toUpperCase())
  ) {
    return app.applicant.emailAddress;
  } else if (
    phoneNumberSearchTextRegex.test(searchText) &&
    app.applicant.phoneNumber
      .toUpperCase()
      .includes(searchText.replace(/\D/g, ""))
  ) {
    return app.applicant.phoneNumber;
  }
  return null;
}

function AppDescription({
  app,
  searchText,
}: {
  app: ApplicationSearchResult;
  searchText: string;
}) {
  const matchedAppText = getMatchedAppText(app, searchText);
  return (
    <>
      <strong>{app.purchaseRecord?.policyNumber ?? "Application"}</strong> -{" "}
      {app.applicant.fullName} - {app.case?.caseName ?? "TBD"}{" "}
      {matchedAppText !== null && <>({matchedAppText})</>}
    </>
  );
}

function SearchFieldset() {
  const history = useHistory();

  const [rawSearchText, setRawSearchText] = useState<string | null>(null);
  const debouncedSearchText = useDebounce(rawSearchText, debounceTimeoutMS);
  const isDebouncing = debouncedSearchText !== rawSearchText;
  const searchText = (debouncedSearchText ?? "").trim();

  const { data, error } = useQuery(searchApplicationsQuery, {
    variables: { input: { searchText, resultsPerPage: 10 } },
    skip: searchText === "",
  });
  const autocompleteItems =
    searchText === "" || error
      ? undefined
      : isDebouncing || !data
      ? [loadingOption]
      : data.searchApplications.applications.map(app => ({
          key: app.id.toString(),
          value: app.id.toString(),
          children: <AppDescription app={app} searchText={searchText} />,
          onClick: () =>
            history.push(
              AdminPaths.ApplicationDetails({ app: { id: app.id } }),
            ),
        }));
  return (
    <div className="box" style={{ textAlign: "center" }}>
      <div>
        <em>
          Search Applicant, Company or Case Name; Email, Phone, or Policy
          Number.
        </em>
      </div>
      <div className="field has-text-left" style={{ marginTop: 5 }}>
        <Wordwheel
          fieldProps={createFieldProps({
            name: "searchText",
            value: rawSearchText,
            setValue: setRawSearchText,
          })}
          autocompleteItems={autocompleteItems}
          leftIconContent={<FontAwesomeIcon icon={faSearch} />}
          placeholder="Search policies and/or applications"
          style={{ flexGrow: 1 }}
          footer={
            <div className="buttons is-centered">
              <SubmitButton
                onMouseDown={() => {
                  history.push(AdminPaths.SearchResults({ searchText }));
                }}
              >
                Search
              </SubmitButton>
            </div>
          }
        />
      </div>
      <Link
        className="button is-primary"
        to={AdminPaths.SearchResults({ searchText: rawSearchText ?? "" })}
      >
        <ButtonIcon style={{ marginRight: 5 }} icon={faMagnifyingGlass} />
        Advanced Search
      </Link>
    </div>
  );
}

function ManageAccountsFieldset() {
  return (
    <div className="box" style={{ textAlign: "center" }}>
      <Link className="button is-primary" to={AdminPaths.ManageAccounts}>
        <ButtonIcon icon={faUsers} />
        <span>Manage Admin Accounts</span>
      </Link>
    </div>
  );
}
